export const therapistsData = {
  industry: "Therapists",
  slug: "therapists",
  
  // SEO Meta Tags
  seo: {
    title: "AI Intake Forms for Therapists | HeyIntake",
    description: "Secure, AI-assisted intake forms for therapists and counselors. Collect client history, assess fit, and prepare for meaningful first sessions.",
    keywords: [
      "therapist client intake form",
      "mental health intake software",
      "counselor intake management",
      "therapy client onboarding",
      "psychologist intake forms",
      "HIPAA compliant intake"
    ]
  },

  heroTitle: "Client Intake Forms for Therapists & Counselors",
  heroSubtitle: "Collect client information securely, assess fit, and prepare for meaningful first sessions.",
  heroDescription: "Whether you're a psychologist, counselor, marriage therapist, or mental health practitioner, HeyIntake helps you gather essential intake information while maintaining client confidentiality.",
  
  // NEW: Why AI Scoring section
  whyAIScoring: {
    title: "Why Therapists Need Better Intake Processes",
    intro: "Effective therapy begins with understanding. The more you know before the first session, the more therapeutic that first session can be.",
    paragraphs: [
      "Traditional therapy intake wastes precious clinical time. You spend half the first session collecting basic information — medication lists, family history, previous treatment. Meanwhile, the client is anxious and the therapeutic rapport you could be building takes a backseat to paperwork.",
      "HeyIntake transforms this process. Clients provide comprehensive intake information before they walk in the door. Our AI helps organize this information, highlighting key themes and potential areas of focus — while you maintain full clinical judgment and control.",
      "For psychologists, counselors, marriage and family therapists, and mental health practitioners, this means more productive first sessions, better therapeutic fit assessment, and more time for what matters: helping clients heal and grow."
    ]
  },
  
  painPoints: [
    {
      title: "Incomplete Client History",
      description: "Starting therapy without a complete picture of the client's background, previous treatment, or current concerns."
    },
    {
      title: "First Session Spent on Paperwork",
      description: "Using valuable session time to collect basic information instead of building rapport."
    },
    {
      title: "Specialty Mismatch",
      description: "Discovering mid-session that the client's needs fall outside your area of expertise."
    },
    {
      title: "Missing Emergency Contacts",
      description: "Not having crisis contacts or safety information when you need it most."
    },
    {
      title: "Insurance Verification Delays",
      description: "Scrambling to verify coverage and collect payment information before sessions."
    },
    {
      title: "No Waitlist Management",
      description: "Losing track of prospective clients when your schedule is full."
    }
  ],

  exampleFields: [
    {
      category: "Contact Information",
      fields: [
        "Full name and preferred name",
        "Date of birth",
        "Contact phone and email",
        "Emergency contact information",
        "Preferred pronouns"
      ]
    },
    {
      category: "Presenting Concerns",
      fields: [
        "What brings you to therapy today?",
        "When did these concerns begin?",
        "Previous therapy experience",
        "What are you hoping to achieve?",
        "Current stress level (1-10)"
      ]
    },
    {
      category: "History & Background",
      fields: [
        "Current medications",
        "Medical conditions",
        "Family mental health history",
        "Substance use history",
        "Previous psychiatric hospitalizations"
      ]
    },
    {
      category: "Logistics & Insurance",
      fields: [
        "Insurance provider and ID",
        "Preferred session times",
        "In-person or telehealth preference",
        "Consent to treatment",
        "HIPAA acknowledgment"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Therapy Intake Form",
    description: "Here's what a professional therapy intake looks like:",
    fields: [
      { label: "What brings you to therapy?", type: "select", options: ["Anxiety", "Depression", "Relationship issues", "Trauma/PTSD", "Life transitions", "Grief/Loss", "Other"] },
      { label: "Tell us more about what you're experiencing", type: "textarea" },
      { label: "Have you been to therapy before?", type: "select", options: ["No, first time", "Yes - helpful", "Yes - not helpful", "Yes - long ago"] },
      { label: "Preferred therapy style", type: "select", options: ["Talk therapy", "CBT/Skills-based", "EMDR", "No preference", "Not sure"] },
      { label: "Preferred session format", type: "select", options: ["In-person", "Video/Telehealth", "Either is fine"] },
      { label: "Insurance or self-pay?", type: "select", options: ["Insurance (will provide details)", "Self-pay", "Not sure yet"] },
      { label: "Scheduling preference", type: "select", options: ["Weekday mornings", "Weekday afternoons", "Evenings", "Weekends", "Flexible"] }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // NEW: How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Therapists",
    subtitle: "From inquiry to prepared first session in three simple steps",
    steps: [
      {
        title: "Clients Share Their Story",
        description: "Prospective clients complete your secure intake form with background and presenting concerns."
      },
      {
        title: "AI Organizes Information",
        description: "Our AI helps organize intake data and highlight key themes — you maintain full clinical judgment."
      },
      {
        title: "Begin Therapy Prepared",
        description: "Start first sessions with context, so you can focus on connection and therapeutic work."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Assisted Intake Assessment for Therapists",
    description: "HeyIntake's AI helps you organize client information, identify key themes, and prepare for more effective first sessions — while you maintain full clinical judgment.",
    factors: [
      "Presenting concern clarity and urgency",
      "Treatment history and previous approaches",
      "Safety and risk indicators",
      "Scheduling and logistics readiness",
      "Insurance verification status"
    ],
    exampleLead: {
      name: "Confidential Client",
      context: "Anxiety & Work Stress • New to Therapy • Evening Availability",
      summary: "Clear presenting concern (work-related anxiety), no previous therapy, motivated to start, insurance verified, evening availability matches openings. Good fit for initial assessment.",
      score: 78
    }
  },

  useCases: [
    {
      icon: "file",
      title: "Secure Intake Collection",
      description: "Collect sensitive client information through secure forms before the first session."
    },
    {
      icon: "brain",
      title: "Prepare for First Sessions",
      description: "Review organized client information so you can focus on connection, not paperwork."
    },
    {
      icon: "users",
      title: "Assess Therapeutic Fit",
      description: "Understand client needs and concerns before committing to an ongoing therapeutic relationship."
    },
    {
      icon: "clock",
      title: "Manage Your Waitlist",
      description: "Track prospective clients and follow up when openings become available."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" },
    { name: "Intake Forms for Lawyers", path: "/intake-form-for-lawyers" },
    { name: "Intake Forms for Accountants", path: "/intake-form-for-accountants" }
  ],

  ctaText: "Ready to Streamline Your Client Intake?"
};
